import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-route <head> manager for the SPA. The static index.html shell ships a
// single title/meta/canonical for every route; without this, all 51 routes
// reported the homepage's title/meta and a homepage canonical (Ahrefs flagged
// 51 "non-canonical page in sitemap" errors; Google treated every service/blog
// page as a duplicate of the homepage). This sets a correct self-referential
// canonical plus a unique title + meta description per route. Google executes
// JS and reads the rendered head, so these are honored for indexing.

const SITE = "https://topnotch-contractors.com";
const DEFAULT_DESC =
  "Baltimore kitchen, bath & basement remodeling since 2010. Maryland MHIC #05-147079, in-house team, line-itemized estimates across Baltimore City & County.";
const DEFAULT_TITLE = "Top Notch Contractors — Baltimore Remodeling";

// Exact-path metadata for the static routes.
const ROUTES = {
  "/": {
    title: "Baltimore Kitchen, Bath & Basement Remodeling | Top Notch",
    description: DEFAULT_DESC,
  },
  "/services": {
    title: "Baltimore Remodeling Services — Kitchen, Bath, Basement",
    description:
      "Kitchen, bathroom, basement & whole-house remodeling across Baltimore County. Licensed (MHIC #05-147079), insured, in-house crews, line-itemized quotes.",
  },
  "/portfolio": {
    title: "Baltimore Remodeling Portfolio | Top Notch Contractors",
    description:
      "Completed kitchen, bath, and basement remodels across Baltimore City and County by Top Notch Contractors. Real local projects since 2010.",
  },
  "/about": {
    title: "About Top Notch Contractors — Baltimore Remodelers",
    description:
      "Remodeling Baltimore homes since 2010. Maryland-licensed (MHIC #05-147079), in-house team, transparent line-itemized estimates. Kitchens, baths, basements.",
  },
  "/blog": {
    title: "Baltimore Remodeling Tips & Cost Guides | Top Notch",
    description:
      "Practical Baltimore remodeling guides: permit costs, kitchen and bath budgets, basement finishing, and contractor advice from Top Notch Contractors.",
  },
  "/contact": {
    title: "Contact Top Notch — Free Baltimore Remodeling Quote",
    description:
      "Request a free Baltimore remodeling consultation. Call Top Notch Contractors or send project details for a line-itemized estimate. MHIC #05-147079.",
  },
  "/areas": {
    title: "Baltimore Remodeling Service Areas | Top Notch",
    description:
      "Top Notch Contractors serves Baltimore City and County — kitchens, baths, basements, and whole-house renovations. Licensed, insured, local since 2010.",
  },
  "/pay": {
    title: "Pay Your Invoice | Top Notch Contractors",
    description: "Securely pay your Top Notch Contractors invoice online.",
    noindex: true,
  },
};

function titleFromSlug(slug) {
  return slug
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Pattern metadata for dynamic routes (/services/:slug, /blog/:slug, /areas/:slug).
function dynamicMeta(parts) {
  const label = titleFromSlug(parts[1]);
  if (parts[0] === "services") {
    return {
      title: `${label} Remodeling in Baltimore | Top Notch`,
      description: `${label} remodeling in Baltimore by Top Notch Contractors — licensed (MHIC #05-147079), insured, in-house crews, line-itemized estimates. Baltimore City & County.`,
    };
  }
  if (parts[0] === "blog") {
    return {
      title: label,
      description: `${label} — remodeling guidance for Baltimore homeowners from Top Notch Contractors.`,
    };
  }
  if (parts[0] === "areas") {
    return {
      title: `${label} Remodeling Contractor | Top Notch`,
      description: `Kitchen, bath & basement remodeling in ${label} by Top Notch Contractors. Licensed, insured, serving the Baltimore area since 2010.`,
    };
  }
  return null;
}

function upsertMeta(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setRobots(noindex) {
  const existing = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (existing) {
      existing.setAttribute("content", "noindex,follow");
    } else {
      const el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("content", "noindex,follow");
      document.head.appendChild(el);
    }
  } else if (existing) {
    existing.remove();
  }
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    let path = pathname.replace(/\/+$/, "");
    if (path === "") path = "/";

    const parts = path.split("/").filter(Boolean);
    const meta = ROUTES[path] || (parts.length === 2 ? dynamicMeta(parts) : null) || {};

    document.title = meta.title || DEFAULT_TITLE;
    upsertMeta("description", meta.description || DEFAULT_DESC);
    upsertCanonical(SITE + (path === "/" ? "/" : path));
    setRobots(!!meta.noindex);
  }, [pathname]);

  return null;
}
